import { useEffect, useRef } from 'react'
import { format } from 'date-fns'
import { cs } from 'date-fns/locale'
import { useSimilarOffersQuery } from '@/api/accommodation/hooks'
import { type SimilarOfferDifference } from '@/api/accommodation/types'
import { useLocationSearchParams } from '@/features/accommodation/hooks/useLocatiomSearchParams'
import { trackSearchSimilarShown } from '@/lib/track'
import SearchResultCard from './searchResultCard'
import { FACILITY_LABELS } from '../utils/facilityOptions'

/** Up to this many results the search also offers similar places. */
export const SIMILAR_MAX_RESULTS = 6

function describeDifference(difference: SimilarOfferDifference): string {
  switch (difference.type) {
    case 'facility': {
      const label = FACILITY_LABELS[difference.facility] ?? difference.facility
      // "Sauna" → "Chybí sauna", but "TV" stays "Chybí TV".
      const lowered = /^\p{Lu}\p{Ll}/u.test(label) ? label.charAt(0).toLowerCase() + label.slice(1) : label
      return `Chybí ${lowered}`
    }
    case 'label':
      return `Mimo: ${difference.name}`
    case 'category':
      return `Jiný typ: ${difference.name}`
    case 'region':
      return `Jiný region: ${difference.name}`
    case 'date': {
      const days = Math.abs(difference.shift)
      const shift = `O ${days === 1 ? 'den' : `${days} dny`} ${difference.shift < 0 ? 'dřív' : 'později'}`
      const arrivalFormat =
        difference.arrival.getMonth() === difference.departure.getMonth() ? 'EEEEEE d.' : 'EEEEEE d. M.'
      return `${shift}: ${format(difference.arrival, arrivalFormat, { locale: cs })}–${format(difference.departure, 'EEEEEE d. M.', { locale: cs })}`
    }
  }
}

interface SearchSimilarProps {
  resultsCount: number
}

/**
 * Under a search with few results: places that miss one or two of the
 * search's criteria, each card saying what it misses.
 */
export function SearchSimilar({ resultsCount }: SearchSimilarProps) {
  const { filters } = useLocationSearchParams()
  const { data, isPending, isError } = useSimilarOffersQuery(filters)
  const trackedDataRef = useRef<unknown>(null)

  useEffect(() => {
    if (!data || data.length === 0 || trackedDataRef.current === data) return
    trackedDataRef.current = data
    const differenceTypes = [...new Set(data.flatMap((similar) => similar.differences.map((d) => d.type)))]
    trackSearchSimilarShown(data.length, resultsCount, differenceTypes, filters)
  }, [data, resultsCount, filters])

  if (isPending || isError || data.length === 0) {
    return null
  }

  return (
    <section className={resultsCount > 0 ? 'mt-12' : undefined}>
      <div className="mb-4 flex items-center gap-4 text-xs font-semibold tracking-wider text-slate-500 uppercase before:h-px before:flex-1 before:bg-slate-200 after:h-px after:flex-1 after:bg-slate-200">
        Mimo vaše filtry
      </div>
      <h2 className="text-lg font-semibold text-slate-900 lg:text-xl">
        Přesně tohle nemáme, ale tohle je hodně blízko
      </h2>
      <p className="mb-6 text-sm text-slate-500">Každé místo splňuje všechny vaše filtry kromě jednoho, nejvýš dvou.</p>
      <div className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
        {data.map((similar, position) => (
          <SearchResultCard
            key={similar.offer.unit.id}
            searchResult={similar.offer}
            position={position}
            source="similar"
            differences={similar.differences.map(describeDifference)}
            differenceTypes={similar.differences.map((d) => d.type)}
          />
        ))}
      </div>
    </section>
  )
}
